import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { SITE } from "@/config/site";

interface Helpline {
  id: string;
  number: string;
  label: string;
  desc: string;
  icon: string;
}

const primary = [
  { id: "p1", number: "112", label: "All-in-one emergency", desc: "Single number for police, fire and ambulance across India.", icon: "ri-alarm-warning-line" },
  { id: "p2", number: "108", label: "Ambulance", desc: "Free emergency ambulance and medical response.", icon: "ri-heart-pulse-line" },
  { id: "p3", number: "100", label: "Police", desc: "Police control room for immediate assistance.", icon: "ri-police-car-line" },
  { id: "p4", number: "101", label: "Fire & rescue", desc: "Fire brigade and rescue services.", icon: "ri-fire-line" },
];

const groups: { id: string; title: string; icon: string; items: Helpline[] }[] = [
  {
    id: "medical",
    title: "Medical",
    icon: "ri-hospital-line",
    items: [
      { id: "m1", number: "108", label: "Ambulance", desc: "Emergency medical transport.", icon: "ri-heart-pulse-line" },
      { id: "m2", number: "102", label: "Pregnancy & child transport", desc: "Maternal and child emergency transport.", icon: "ri-parent-line" },
      { id: "m3", number: "104", label: "Health helpline", desc: "Health information and guidance.", icon: "ri-stethoscope-line" },
      { id: "m4", number: "1064", label: "Blood bank", desc: "Blood availability and donation enquiries.", icon: "ri-drop-line" },
    ],
  },
  {
    id: "safety",
    title: "Safety & support",
    icon: "ri-shield-user-line",
    items: [
      { id: "s1", number: "1091", label: "Women's helpline", desc: "Support for women in distress.", icon: "ri-women-line" },
      { id: "s2", number: "1098", label: "Child helpline", desc: "Help for children in need of care.", icon: "ri-hand-heart-line" },
      { id: "s3", number: "181", label: "Women in crisis", desc: "24x7 support for women in distress.", icon: "ri-shield-star-line" },
      { id: "s4", number: "1090", label: "Senior citizen helpline", desc: "Assistance for the elderly.", icon: "ri-parent-fill" },
    ],
  },
  {
    id: "civic",
    title: "Civic & utilities",
    icon: "ri-building-2-line",
    items: [
      { id: "cv1", number: "1912", label: "Electricity (CESCOM)", desc: "Power supply and outage complaints.", icon: "ri-plug-2-line" },
      { id: "cv2", number: "1076", label: "District administration", desc: "Grievances for local administration.", icon: "ri-government-line" },
      { id: "cv3", number: "1533", label: "Roads & highways", desc: "Highway and road assistance.", icon: "ri-roadster-line" },
      { id: "cv4", number: "1909", label: "Traffic", desc: "Traffic-related complaints and assistance.", icon: "ri-car-line" },
    ],
  },
];

const prepared = [
  { id: "t1", icon: "ri-map-pin-2-line", title: "Know your location", text: "Share your exact locality or a landmark — it helps responders reach you faster." },
  { id: "t2", icon: "ri-mic-line", title: "Stay calm, speak clearly", text: "State what happened, how many people are involved and whether anyone is injured." },
  { id: "t3", icon: "ri-phone-line", title: "Keep the line open", text: "Don't hang up until the operator confirms they have your details and tells you to." },
  { id: "t4", icon: "ri-shield-check-line", title: "Don't self-deploy", text: "For fires or accidents, move to safety and leave the response to trained teams." },
];

export default function EmergencyServices() {
  return (
    <>
      <Seo
        title="Emergency Services & Helpline Numbers"
        description="Important emergency and helpline numbers for Mysore, Mandya, Chamarajanagar and Kodagu — ambulance, police, fire, medical, women's, child and civic helplines in one place."
        keywords="Mysore emergency numbers, ambulance 108, police 100, fire 101, helpline Karnataka, emergency contacts Mysore"
        path="/emergency-services"
      />

      <div className="relative w-full">
        <div className="absolute inset-0 bg-secondary-900"></div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16">
          <div className="[&_a]:text-secondary-100 [&_span]:text-secondary-100 [&_.text-foreground-800]:text-accent-50">
            <Breadcrumbs items={[{ label: "Emergency Services" }]} />
          </div>
          <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-background-50">
            <i className="ri-alarm-warning-line"></i> In an emergency, always dial 112 first
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold tracking-tight text-background-50">
            Emergency services &amp; helplines
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-lg text-secondary-100 leading-relaxed">
            Keep these numbers handy for Mysore, Mandya, Chamarajanagar and Kodagu. In a real emergency, call{" "}
            <strong className="font-bold text-background-50">112</strong> — it connects you to police, fire and ambulance.
          </p>
          <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {primary.map((p) => (
              <a
                key={p.id}
                href={`tel:${p.number}`}
                className="group rounded-lg border border-secondary-700 bg-secondary-800/60 p-4 backdrop-blur transition-colors hover:border-primary-400 hover:bg-secondary-800 cursor-pointer"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <div className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-background-50">{p.number}</div>
                <div className="mt-1 text-xs font-semibold text-primary-300">{p.label}</div>
                <p className="mt-1.5 text-xs text-secondary-100/90 leading-relaxed">{p.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.id}>
              <h2 className="inline-flex items-center gap-2 font-heading text-lg md:text-xl font-extrabold text-foreground-950">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${g.icon} text-lg`}></i>
                </span>
                {g.title}
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {g.items.map((h) => (
                  <a
                    key={h.id}
                    href={`tel:${h.number}`}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${h.icon} text-lg`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block font-heading text-lg font-extrabold text-foreground-950">{h.number}</span>
                      <span className="block text-sm font-semibold text-foreground-800">{h.label}</span>
                      <span className="mt-0.5 block text-xs text-foreground-500 leading-relaxed">{h.desc}</span>
                    </span>
                  </a>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">Be prepared</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {prepared.map((p) => (
              <div key={p.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-heading text-lg md:text-2xl font-extrabold text-foreground-950">
              Looking for hospitals, clinics or blood banks nearby?
            </h2>
            <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed">
              Browse verified medical businesses in your district. You can filter by rating and whether they're
              open right now.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/category/healthcare"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-hospital-line"></i> Hospitals &amp; clinics
            </Link>
            <Link
              to="/directory?q=pharmacy&open=1"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-store-3-line"></i> Open pharmacies
            </Link>
          </div>
        </section>

        <p className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50 p-4 text-xs text-secondary-900 leading-relaxed">
          <i className="ri-information-line mr-1"></i>
          Helpline numbers are provided for quick reference and may change. For anything life-threatening, dial{" "}
          <strong>112</strong> immediately. Spotted an outdated number?{" "}
          <Link to="/contact" className="font-semibold underline">Let us know</Link> and we'll correct it.
          Managing a medical business? Get listed so people can find you at{" "}
          <a href={`tel:${SITE.phoneHref}`} className="font-semibold underline">{SITE.phone}</a>.
        </p>
      </div>
    </>
  );
}